import { createServer, Response } from 'miragejs'
import MSWInterceptor from 'mirage-msw'
import { pluralize, singularize } from '@ember-data/request-utils/string'
import { studentProgressRoute } from '@blakeelearning/student-progress/mirage/config'

import ENV from 'teacher-ui-client/config/environment'
import { encodeJWT } from 'teacher-ui-client/tests/helpers/authentication-helper'
import { join } from 'teacher-ui-client/utils/uri'

import { camelize } from '@ember/string'
import { isPresent, isEmpty } from '@ember/utils'

import { range } from 'lodash'
import { parse, addDays, format } from 'date-fns'

import { setStudentEventsEndpoints } from 'teacher-ui-client/mirage/handlers/student-events'
import { setAssignmentEndpoints } from 'teacher-ui-client/mirage/handlers/assignments'
import { setReportingEndpoints } from 'teacher-ui-client/mirage/handlers/reporting'
import { setLessonStandardsEndpoints } from 'teacher-ui-client/mirage/handlers/lesson-standards'
import { setWritingLegendsEndpoints } from 'teacher-ui-client/mirage/handlers/writing-legends'
import { setRecentActivityEndpoints } from 'teacher-ui-client/mirage/handlers/gravity/widgets/recent-activity'
import { setUpcomingAssignmentsEndpoints } from 'teacher-ui-client/mirage/handlers/upcoming-assignments'
import { setClassesAtAGlanceEndpoints } from 'teacher-ui-client/mirage/handlers/classes-at-a-glance'
import { setNeedsAttentionStudentsEndpoints } from 'teacher-ui-client/mirage/handlers/gravity/widgets/needs-attention-students'
import { gradeSetAu } from 'teacher-ui-client/mirage/fixtures/grade-set'
import {
  reLessonInfoReading,
  reLessonInfoSpelling,
  reLessonInfoStorylands,
  reLessonInfoDrivingTests,
  rexLessonInfoSpelling,
  rexLessonInfoMyLesson,
  fpLessonInfoLessons,
  mathsLessonInfoLessons,
  mathsDrivingTestsLessons,
  mathsMentalMinuteLessons,
  wlLessonInfoWriting,
} from 'teacher-ui-client/mirage/fixtures/lesson-info'

import { contentfulEntriesProxyUrl } from 'teacher-ui-client/mirage/handlers/contentful/index'
import combineHandlers from 'teacher-ui-client/mirage/handlers/combine'
import handleCreateRequest from 'teacher-ui-client/mirage/handlers/create'
import handleUniqueBy from 'teacher-ui-client/mirage/handlers/uniqueBy'
import { setSchoolRolloverEndpoints } from 'teacher-ui-client/mirage/handlers/school-rollover'
import { fakeBookData } from 'teacher-ui-client/mirage/handlers/book-data'

// Only need to import those called by loadFixtures()
import readingScalesFixture from 'teacher-ui-client/mirage/fixtures/book-library/reading-scales'
import assignmentsFixture from 'teacher-ui-client/mirage/fixtures/assignments'

const mirageModels = import.meta.glob('../models/**/*.{js,ts}', { eager: true })
const mirageFactories = import.meta.glob('../factories/**/*.{js,ts}', { eager: true })
const mirageScenarios = import.meta.glob('../scenarios/*.{js,ts}', { eager: true })
const mirageSerializers = import.meta.glob('../serializers/**/*.{js,ts}', { eager: true })

/**
 * mirage expects each item to be a camelcased key value pair
 *
 * From
 * {
 *   '../models/candidate-student.js': importedFile,
 *   '../models/book-library/book-metadata.js': importedFile,
 *   ...
 * }
 *
 * to this
 * {
 *   'candidateStudent': importedFile,
 *   'bookLibrary/bookMetadata': importedFile,
 *   ...
 * }
 */
function mapToCamelCasedKeys(importGlobs) {
  return Object.fromEntries(
    Object.entries(importGlobs).map(([filenameKey, value]) => {
      // strip off the dir prefix and extension
      const name = filenameKey
        .replace(/\.\.\/(models|factories|scenarios|serializers)\//, '')
        .replace('.js', '')
        .replace('.ts', '')
      // example: book-library/book-metadata -> bookLibrary/bookMetadata
      const camelCased = name.split('/').map(camelize).join('/')
      return [camelCased, value.default]
    }),
  )
}

const { gravityV3Url, gravityCsvUrl } = ENV

function setLessonsAPIEndpoints(config) {
  config.get(join(ENV.lessonsApiV1Url, 're', 'lesson_info').href, function (schema, request) {
    const productAndPrecinct = `${request.queryParams.product} ${request.queryParams.precinct}`

    switch (productAndPrecinct) {
      case 're lessons':
        return reLessonInfoReading
      case 're spelling':
        return reLessonInfoSpelling
      case 're storylands':
        return reLessonInfoStorylands
      case 're driving_tests':
        return reLessonInfoDrivingTests
      default:
        return null
    }
  })
  config.get(join(ENV.lessonsApiV1Url, 'rex', 'lesson_info').href, function (schema, request) {
    const productAndPrecinct = `${request.queryParams.product} ${request.queryParams.precinct}`

    switch (productAndPrecinct) {
      case 'rex my_lessons':
        return rexLessonInfoMyLesson
      case 'rex english_skills_spelling':
        return rexLessonInfoSpelling
      default:
        return null
    }
  })
  config.get(join(ENV.lessonsApiV1Url, 'fast_phonics', 'lesson_info').href, function (schema, request) {
    const productAndPrecinct = `${request.queryParams.product} ${request.queryParams.precinct}`

    switch (productAndPrecinct) {
      case 'fast_phonics lessons':
        return fpLessonInfoLessons
      default:
        return null
    }
  })
  config.get(join(ENV.lessonsApiV1Url, 'maths', 'lesson_info').href, function (schema, request) {
    const productAndPrecinct = `${request.queryParams.product} ${request.queryParams.precinct}`

    switch (productAndPrecinct) {
      case 'maths lessons':
        return mathsLessonInfoLessons
      case 'maths driving_tests':
        return mathsDrivingTestsLessons
      case 'maths mental_minute':
        return mathsMentalMinuteLessons
      default:
        return null
    }
  })
  config.get(join(ENV.lessonsApiV1Url, 'writing_legends', 'lesson_info').href, function (schema, request) {
    const productAndPrecinct = `${request.queryParams.product} ${request.queryParams.precinct}`

    switch (productAndPrecinct) {
      case 'writing_legends lessons':
        return wlLessonInfoWriting
      default:
        return null
    }
  })
}

function setSharedReadingeggsEndpoints(config) {
  config.get(join(ENV.readingEggsV1Url, 'grade_set').href, function () {
    return gradeSetAu
  })
}

function setTeacherUiEndpoints(config) {
  const makeUrl = (...paths) => join(ENV.readingEggsV1Url, 'teacher_ui', ...paths).href

  config.post(makeUrl('/:re_or_ms/contact-us'), function () {
    return new Response(204)
  })

  config.post(makeUrl('/students/download_parent_letters'), function () {
    return ''
  })
  config.post(makeUrl('/teachers/download_parent_letters'), function () {
    return ''
  })
}

function setAuth(config) {
  config.get(ENV.jwtURL, function ({ teachers }) {
    const teacher = teachers.first()

    // expiry = time since epoch + 7 days (in milliseconds)
    const exp = addDays(new Date(), 7).getTime() / 1000 // Divide by 1000 because backend sends value in seconds

    const payload = {
      exp,
      data: {
        id: Number.parseInt(teacher.id, 10),
        roles: ['subco-reading', 'subco-maths'],
        school_id: Number.parseInt(teacher.schoolId, 10),
        type: 'teacher',
      },
    }

    return encodeJWT(payload)
  })
}

function bookLibrary(server) {
  server.get(`${ENV.libraryBookDataUrl}:book_code`, function (_schema, request) {
    return new Response(200, { 'content-type': 'application/json' }, fakeBookData(request.params.book_code))
  })
  /**
   * Note, this mirage endpoint doesn't implement _all_ of the features of the real endpoint.
   * For example, while it implements groupOffset and groupLimit, to enable the find a book page,
   * it does not yet implement `page[number]` or `page[size]` as it's not really necessary for
   * development at this stage.
   */
  server.get(`${ENV.libraryApiUrl}books`, function (schema, { queryParams }) {
    const keywords = queryParams['filter[keywords]']
    const name = queryParams['filter[name]']
    const searchKey = queryParams['filter[searchKey]']
    const code = queryParams['filter[code]']
    const [from, to] = queryParams['filter[searchValue]']?.split(':') || []
    const searchCategories = queryParams['filter[categories]']?.split(',') || []
    // Only allow blake reading level, and reading age search keys
    let bookAttrForSearch = null
    if (searchKey === 'blake-reading-level' || searchKey === 'reading-age') {
      bookAttrForSearch = camelize(searchKey)
    }

    // used to determine if the book has the selected reading scale value within the search range
    const isBookInSearchScope = (book, metaAttr, min, max) => {
      let bookValue = -1
      if (metaAttr) bookValue = book.bookMetadata[metaAttr]
      return bookValue >= Number(min) && bookValue <= Number(max)
    }

    // Figure out what books satisfy this request
    const allRelevantBooks = schema['bookLibrary/books'].all().filter((book) => {
      const inScope = isPresent(bookAttrForSearch) ? isBookInSearchScope(book, bookAttrForSearch, from, to) : true

      // support classification.* (eg 'fiction.*' / 'nonfiction.*')
      // support classification.category
      const skipCategoryCheck = isEmpty(searchCategories)
      const matchesCategories =
        skipCategoryCheck ||
        searchCategories?.some((searchCategory) => {
          const [categoryFilterType, categoryId] = searchCategory.split('.')
          const includeAllCategories = categoryId === '*'

          const isClassificationMatch = book.classification === categoryFilterType
          const isCategoryMatch = book.primaryCategories.includes(categoryId)

          return includeAllCategories ? isClassificationMatch : isClassificationMatch && isCategoryMatch
        })

      // keywords
      const hasMatchedKeywords = isPresent(keywords) ? JSON.stringify(book.bookMetadata.attrs).includes(keywords) : true
      const hasMatchedName = isPresent(name) ? book.bookMetadata.attrs.name.includes(name) : true
      const hasMatchedCode = isPresent(code) ? book.attrs.code.includes(code) : true
      return inScope && hasMatchedKeywords && hasMatchedName && matchesCategories && hasMatchedCode
    })

    // Now apply some basic pagination

    // first check this was a request for multiple books, with a groupLimit
    // don't bother too much about strictly checking for integer values.. it's only mirage
    const limit = queryParams.groupLimit ? Number(queryParams.groupLimit) : 0
    const offset = queryParams.groupOffset ? Number(queryParams.groupOffset) : 0

    if (limit > 0 && bookAttrForSearch) {
      // paginate
      const start = offset
      const end = start + limit
      const booksPage = allRelevantBooks.slice(start, end) // inclusive, exclusive
      // serialize
      const serializedPage = this.serialize(booksPage)

      // Pagination groups

      const fictionFilter = (bookItems) => bookItems.filter((b) => b.classification === 'fiction')
      const nonfictionFilter = (bookItems) => bookItems.filter((b) => b.classification === 'nonfiction')

      const searchValueRange = range(Number(from), Number(to) + 1) // an array of integers in the from/to range (inclusive)

      // For each search value, return a fiction and nonfiction group, that has meta for that classification + search value
      const groups = searchValueRange.flatMap((searchValue) => {
        const booksForSearchValue = allRelevantBooks.filter((book) => {
          return isBookInSearchScope(book, bookAttrForSearch, searchValue, searchValue)
        })
        const fictionBooksForSearchValue = fictionFilter(booksForSearchValue)
        const nonFictionBooksForSearchValue = nonfictionFilter(booksForSearchValue)
        const fictionBooksPageForSearchValue = fictionBooksForSearchValue.slice(start, end)
        const nonFictionBooksPageForSearchValue = nonFictionBooksForSearchValue.slice(start, end) // inclusive, exclusive

        return [
          {
            classification: 'fiction',
            search_value: searchValue,
            total: fictionBooksForSearchValue.length,
            count: fictionBooksPageForSearchValue.length,
            offset,
          },
          {
            classification: 'nonfiction',
            search_value: searchValue,
            total: nonFictionBooksForSearchValue.length,
            count: nonFictionBooksPageForSearchValue.length,
            offset,
          },
        ]
      })

      // Now add the meta key to the serialized response
      serializedPage.meta = {
        total: allRelevantBooks.length, // total number of books available for this request search scope
        count: booksPage.length, // number of books returned in this request page
        offset,
        groups,
      }
      return serializedPage
    } else {
      return allRelevantBooks
    }
  })
  server.get(`${ENV.libraryApiUrl}books/:id`, 'bookLibrary/books')
  server.patch(`${ENV.libraryApiUrl}books/:id`, 'bookLibrary/books')
  server.get(`${ENV.libraryApiUrl}reading-scales`, 'bookLibrary/readingScales')
  server.get(`${ENV.libraryApiUrl}shares`, 'bookLibrary/shares')
  server.get(`${ENV.libraryApiUrl}shares/:id`, 'bookLibrary/shares')
  server.patch(`${ENV.libraryApiUrl}shares/:id`, 'bookLibrary/shares')
  server.post(`${ENV.libraryApiUrl}shares`, 'bookLibrary/shares')
  server.get(`${ENV.libraryApiUrl}collections`, 'bookLibrary/collections')
  server.get(`${ENV.libraryApiUrl}collections/:id`, 'bookLibrary/collections')
  const uniqueCollectionNameError = new Response(422, {}, { errors: [{ title: 'has already been taken' }] })
  const tooLongCollectionNameError = new Response(422, {}, { errors: [{ code: 'length' }] })
  server.post(`${ENV.libraryApiUrl}collections`, function (schema, _request) {
    const collections = schema['bookLibrary/collections']
    const collectionAttrs = this.normalizedRequestAttrs('book-library/collection')

    if (collections.findBy({ name: collectionAttrs.name })) {
      // Enforce unique name
      return uniqueCollectionNameError
    } else if (collectionAttrs.name.length > 255) {
      // Enforce name length
      return tooLongCollectionNameError
    } else {
      return collections.create({ ...collectionAttrs, lastUpdated: new Date() })
    }
  })
  server.patch(`${ENV.libraryApiUrl}collections/:id`, function (schema, request) {
    const collections = schema['bookLibrary/collections']
    const collectionAttrs = this.normalizedRequestAttrs('book-library/collection')

    const existingCollection = collections.find(request.params.id)
    if (existingCollection.name !== collectionAttrs.name && collections.findBy({ name: collectionAttrs.name })) {
      // Enforce unique name, if they're trying to change the name of the collection
      return uniqueCollectionNameError
    } else if (collectionAttrs.name.length > 255) {
      // Enforce name length
      return tooLongCollectionNameError
    } else {
      return existingCollection.update({ ...collectionAttrs, lastUpdated: new Date() })
    }
  })
  server.del(`${ENV.libraryApiUrl}collections/:id`, function (schema, request) {
    const collections = schema.db['bookLibrary/collections']
    collections.remove(request.params.id)
    return new Response(204)
  })
  server.get(`${ENV.libraryApiUrl}collection-stats`, function (_schema, _request) {
    return {
      data: {
        id: '1',
        attributes: {
          'shared-collection-count': 4,
          'books-in-shared-collections-count': 28,
        },
        type: 'collection-stats',
      },
    }
  })
  server.get(`${ENV.libraryApiUrl}student-stats`, function (schema, _request) {
    const students = schema.students.all().models
    return {
      data: students.map((student) => {
        return {
          id: student.id,
          attributes: {
            'first-name': student.firstName,
            'last-name': student.lastName,
            'grade-position': student.gradePosition,
            'school-class-names': student.schoolClasses?.models?.map((sc) => sc.name).join(', '),
            'active-shares-collection-count': 1,
            'pending-shares-collection-count': 2,
            'expired-shares-collection-count': 5,
          },
          type: 'student-stats',
        }
      }),
    }
  })
}

function gravity(server) {
  server.get(`${gravityV3Url}accents`)

  server.get(`${gravityV3Url}standards-sets`, 'standards-sets')

  server.get(`${gravityV3Url}partners/:partner_id/partner-contact-details`, function ({ partners }, request) {
    const { partner_id } = request.params
    const partner = partners.find(partner_id)
    return partner.partnerContactDetails
  })

  server.get(`${gravityV3Url}schools/:id`)
  server.patch(`${gravityV3Url}schools/:id`)
  server.get(`${gravityV3Url}schools/:id/school-trial-counts/:subscription_type`, function (schema, request) {
    // return record that already exists, or create a new one with some default counts.
    const { id, subscription_type: subscriptionType } = request.params
    const recordId = `${id}_${subscriptionType}`
    return (
      schema.schoolTrialCounts.findBy({
        id: recordId,
      }) ??
      schema.schoolTrialCounts.create({
        id: recordId,
        studentTrialCount: 100,
        teacherTrialCount: 200,
        subscriptionType,
      })
    )
  })
  server.get(`${gravityV3Url}schools/:id/statistics`, function (_schema, request) {
    const schoolId = request.params.id
    return {
      data: {
        id: schoolId,
        attributes: {
          'total-student-count': 333,
        },
        type: 'statistics',
      },
    }
  })

  server.get(`${gravityV3Url}schools/:school_id/school-classes`)
  server.get(`${gravityV3Url}school-classes/:id`)
  server.patch(`${gravityV3Url}school-classes/:id`, function ({ schoolClasses }, request) {
    const { id } = request.params
    const attrs = this.normalizedRequestAttrs()
    const updatedSchoolClass = schoolClasses.find(id).update(attrs)
    if (updatedSchoolClass.teachers.length === 0) {
      updatedSchoolClass.destroy()
      return updatedSchoolClass
    }
    return new Response(204)
  })
  server.post(`${gravityV3Url}school-classes`, handleCreateRequest('schoolClasses'))
  server.del(`${gravityV3Url}school-classes/:id`, function (schema, request) {
    schema.schoolClasses.find(request.params.id).destroy()
    return new Response(204)
  })
  server.del(`${gravityV3Url}school-classes`, function (schema, request) {
    const ids = JSON.parse(request.requestBody).data.map((sc) => sc.id)
    ids.forEach((id) => schema.schoolClasses.find(id).destroy())
    return new Response(204)
  })
  server.del(`${gravityV3Url}students/:id`)
  server.del(`${gravityV3Url}students`, function (_schema, _request) {
    return new Response(204, {}, {})
  })
  server.post(`${gravityV3Url}join-school-class-request/:id`, function (_schema, _request) {
    return new Response(200, {}, {})
  })

  /**
   * @return {Array<SubscriptionUtilisation>} the school or teacher-trial subscription-utilisation for each subscription type
   * @example
   *  [
   *   'maths school subscription utilisation'
   *   'reading trial subscription utilisation for current teacher',
   *  ]
   *
   * @description note that the endpoint doesn't return strings, the example has text descriptions of SubscriptionUtilisation objects
   */
  server.get(`${gravityV3Url}teachers/:id/subscription-utilisations`, function (schema, request) {
    const {
      params: { id: teacherId },
    } = request

    const teacher = schema.teachers.find(teacherId)
    const schoolId = teacher.school.id

    // for each sub type, find all ACTIVE SUBSCRIPTIONS, then find their utilisations, consolidate them.
    const subscriptionTypes = ['reading', 'maths', 'writing', 'wf']

    const now = new Date()

    const utilisations = subscriptionTypes.map((subscriptionType) => {
      // find all ACTIVE SUBS for the given subscription type
      const isActive = (sub) => {
        const sameSubscriptionType = sub.subscriptionType === subscriptionType
        const startDate = parse(sub.startDate, 'yyyy-MM-dd', new Date())
        const endDate = parse(sub.endDate, 'yyyy-MM-dd', new Date())
        return sameSubscriptionType && startDate < now && endDate > now
      }
      const schoolSubs = schema.schoolSubscriptions.where((sub) => isActive(sub) && sub.schoolId === schoolId)
      const teacherSubs = schema.teacherSubscriptions.where((sub) => isActive(sub) && sub.teacherId === teacherId)

      // The backend consolidates the licence counts from subs into a single utilisation, we'll just use the first one
      // that has a utilisation

      const schoolSubUtilisation = schoolSubs.filter((sub) => sub.subscriptionUtilisation).models[0]
        ?.subscriptionUtilisation
      const teacherSubUtilisation = teacherSubs.filter((sub) => sub.subscriptionUtilisation).models[0]
        ?.subscriptionUtilisation

      // if there's a teacher/trial utilisation present, it is used instead of any school ones that might be present

      return teacherSubUtilisation ?? schoolSubUtilisation
    })

    const utilisationIds = utilisations.map((utilisation) => utilisation?.id).filter(Boolean)

    return schema.subscriptionUtilisations.where((utilisation) => utilisationIds.includes(utilisation.id))
  })

  server.get(`${gravityV3Url}teachers/:id`)

  server.get(`${gravityV3Url}teachers/:teacher_id/dashboard-preferences`, function (schema, request) {
    const teacher = schema.teachers.find(request.params.teacher_id)
    return teacher.dashboardPreferences ?? new Response(404)
  })

  server.post(`${gravityV3Url}teachers/:teacher_id/dashboard-preferences`, function (schema, request) {
    const teacher = schema.teachers.find(request.params.teacher_id)
    const attrs = this.normalizedRequestAttrs('dashboard-preferences')
    return schema.dashboardPreferences.create({ ...attrs, teacher })
  })

  server.patch(`${gravityV3Url}teachers/:teacher_id/dashboard-preferences`, function (schema, request) {
    const teacher = schema.teachers.find(request.params.teacher_id)
    const attrs = this.normalizedRequestAttrs('dashboard-preferences')
    return teacher.dashboardPreferences.update(attrs)
  })

  server.post(
    `${gravityV3Url}teachers`,
    combineHandlers(handleUniqueBy('teachers', 'email', 'email has already been taken'), function (schema, request) {
      const handleCreate = handleCreateRequest('teachers')

      const response = handleCreate.call(this, schema, request)
      const teachers = Array.isArray(response.models) ? response.models : [response]

      teachers
        .filter((item) => item.accountType === 'teacher-subscription')
        .forEach((teacher) => {
          const startDate = new Date()
          const endDate = new Date()
          endDate.setDate(endDate.getDate() + 30)
          schema.teacherSubscriptions.create({
            startDate: format(startDate, 'yyyy-MM-dd'),
            endDate: format(endDate, 'yyyy-MM-dd'),
            teacher,
            subscriptionType: 'reading',
          })
        })

      // example Error response from the server
      // return new Response(422, {
      //   'Content-Type': 'application/vnd.api+json',
      // }, {
      //   'jsonapi': { 'version': '1.0' },
      //   'errors': [
      //     { 'title': 'Unprocessable Entity', 'status': 422, 'detail': 'login has already been taken' },
      //     // { 'title': 'Unprocessable Entity', 'status': 422, 'detail': 'Oh no' },
      //   ],
      // })
      return response
    }),
  )

  // Teacher bulk delete endpoint
  server.del(`${gravityV3Url}teachers`, function (schema, request) {
    const ids = JSON.parse(request.requestBody).data.map((t) => t.id)
    ids.forEach((id) => schema.teachers.find(id).destroy())
    return new Response(204)
  })

  server.post(`${gravityV3Url}subscription-coordinators`)
  server.del(`${gravityV3Url}subscription-coordinators/:id`)

  server.patch(`${gravityV3Url}teachers/:id`, function ({ teachers }, request) {
    const { id } = request.params
    const attrs = this.normalizedRequestAttrs()
    return teachers.find(id).update(attrs)
  })

  server.get(`${gravityV3Url}teachers/:teacher_id/students`, function (schema, request) {
    const {
      params: { teacher_id: teacherId },
    } = request
    const teacher = schema.teachers.find(teacherId)

    const { schoolClasses } = teacher
    const studentIds = schoolClasses.models.reduce((list, schoolClass) => {
      return list.concat(schoolClass.studentIds)
    }, [])

    return schema.students.find(studentIds)
  })

  const useAlternativeStudentsResponse = false
  if (useAlternativeStudentsResponse) {
    server.post(`${gravityV3Url}students`, () => {
      return new Response(
        422,
        {
          'Content-Type': 'application/vnd.api+json',
        },
        {
          jsonapi: { version: '1.0' },
          errors: [{ title: 'Unprocessable Entity', status: 422, detail: 'subscription no more student licences' }],
        },
      )
    })
  } else {
    server.post(`${gravityV3Url}students`, handleCreateRequest('students'))
  }

  const useAlternativeStudentResponse = false
  if (useAlternativeStudentResponse) {
    server.patch(`${gravityV3Url}students/:id`, function () {
      // example Error response from the server
      return new Response(
        422,
        {
          'Content-Type': 'application/vnd.api+json',
        },
        {
          jsonapi: { version: '1.0' },
          errors: [
            {
              title: 'Unprocessable Entity',
              status: 422,
              detail: 'login has already been taken',
            },
          ],
        },
      )
    })
  } else {
    server.patch(`${gravityV3Url}students/:id`)
  }

  server.patch(`${gravityV3Url}students`, function () {
    return new Response(204, {}, {})
  })

  server.get(`${gravityV3Url}schools/:school_id/students`, function (schema, _request) {
    return schema.students.all()
  })

  server.post(`${gravityV3Url}schools/:school_id/students`, function (schema, request) {
    // Since this is a POST, we need to handle "includes" a little differently, as its not present in the QPs
    // Setting QP includes here is OK https://miragejs.com/api/classes/serializer/
    const include = JSON.parse(request.requestBody)?.include
    request.queryParams.include ??= include
    return schema.students.all()
  })

  server.get(`${gravityV3Url}schools/:school_id/teachers`, 'teachers')

  server.post(`${gravityV3Url}teacher-subscriptions`, function (schema, _request) {
    const { subscriptionType, teacherId } = this.normalizedRequestAttrs()
    const startDate = new Date()
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + 30)
    const teacherSubscription = schema.teacherSubscriptions.create({
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      teacherId,
      subscriptionType,
    })
    teacherSubscription.createSubscriptionUtilisation({ subscriptionType })
    return teacherSubscription
  })

  server.post(`${gravityV3Url}teacher-password-emails`, function (_schema, _request) {
    return new Response(200, {}, {})
  })

  setSchoolRolloverEndpoints(server, gravityV3Url)

  server.post(`${gravityV3Url}teacher-ui/progression/api-set`, function () {
    return new Response(204)
  })
}

function setImportStudentEndpoints(server) {
  const subcoImportsBaseUrl = `${gravityCsvUrl}schools/:school_id/:subscription_type`
  const teacherImportsBaseUrl = `${gravityCsvUrl}teachers/:teacher_id/:subscription_type`

  let currentJobId = 123456
  // This will be filled up as requests are made to mirage, so that it can return a status when polled for a job id
  const jobsLog = {
    // 1: { status: 'complete', filename: 'valid.csv' },
  }
  const jobStatusData = (jobId, jobType) => {
    const isValidateJob = jobType === 'validate'
    return {
      attributes: {
        status: jobsLog[jobId].status,
      },
      id: jobId,
      type: isValidateJob ? 'import-validations' : 'import-schedules',
    }
  }

  const createJobResponse = (filename, jobType) => {
    currentJobId += 1 // incremement for this request
    jobsLog[currentJobId] = { status: 'scheduled', filename }

    // In 7 seconds, set the job status to complete. 7 seconds is arbitrary, allows for a few polls to occur.
    const currentJob = jobsLog[currentJobId]
    setTimeout(() => {
      currentJob.status = 'complete'
    }, 7000)

    const response = new Response(
      202,
      {},
      {
        jsonapi: { version: '1.0' },
        data: jobStatusData(currentJobId, jobType),
      },
    )
    currentJob.status = 'busy'
    return response
  }

  const validationResultData = () => {
    return [
      {
        attributes: {
          'first-name': 'bob import',
          'last-name': '1',
          'school-class-name': '1',
          'grade-position': 1,
          'teacher-email': 'notanemail@example.com',
          'csv-row': 1,
          'duplicate-destination': false,
          'duplicate-source': false,
        },
        id: '1', // candidate student id
        // type: 'import-validation-result',
        type: 'candidate-student',
      },
    ]
  }

  // Use the filename to determine the outcome of the fake response, for testing purposes
  const validationResultResponse = (filename) => {
    if (filename?.includes('invalid')) {
      return new Response(
        422,
        {},
        {
          jsonapi: { version: '1.0' },
          data: validationResultData(),
          errors: [
            {
              code: 'invalid_attr',
              detail: 'grade_position has invalid format',
              meta: { csv_row: 1, value: null },
              source: { pointer: '/data/0/attributes/grade-position' },
              title: 'Invalid attribute',
            },
          ],
        },
      )
    } else {
      return new Response(
        200,
        {},
        {
          jsonapi: { version: '1.0' },
          data: validationResultData(),
        },
      )
    }
  }

  // Validation job created, if async returns status/jobid else returns validation result
  const importValidationHandler = function (_db, request) {
    if (request.requestBody instanceof FormData) {
      const filename = 'testfile.txt'
      const response = createJobResponse(filename, 'validation')
      return response
    }
  }
  server.post(`${subcoImportsBaseUrl}/import-validation`, importValidationHandler)
  server.post(`${teacherImportsBaseUrl}/import-validation`, importValidationHandler)

  // Job being polled for status
  const importValidationPollHandler = function (_db, request) {
    const jobId = Number(request?.params?.job_id)
    return new Response(
      200,
      {},
      {
        jsonapi: { version: '1.0' },
        data: jobStatusData(jobId, 'validate'),
      },
    )
  }
  server.get(`${subcoImportsBaseUrl}/import-validation/:job_id`, importValidationPollHandler)
  server.get(`${teacherImportsBaseUrl}/import-validation/:job_id`, importValidationPollHandler)

  // Job status should be complete before calling this endpoint. This endpoint returns the csv.
  const importValidationResultHandler = function (_db, request) {
    const jobId = Number(request?.params?.job_id)
    const job = jobsLog[jobId]
    return validationResultResponse(job?.filename)
  }
  server.get(`${subcoImportsBaseUrl}/import-validation-result/:job_id`, importValidationResultHandler)
  server.get(`${teacherImportsBaseUrl}/import-validation-result/:job_id`, importValidationResultHandler)

  // Import job creation
  const importHandler = function (_db, request) {
    if (request.requestBody instanceof FormData) {
      const filename = 'testfile.txt'
      const response = createJobResponse(filename, 'import')
      return response
    }
  }
  server.post(`${subcoImportsBaseUrl}/import-schedules`, importHandler)
  server.post(`${teacherImportsBaseUrl}/import-schedules`, importHandler)

  // Job being polled for status
  const importPollHandler = function (_db, request) {
    const jobId = Number(request?.params?.job_id)
    return new Response(
      200,
      {},
      {
        jsonapi: { version: '1.0' },
        data: jobStatusData(jobId, 'import'),
      },
    )
  }
  server.get(`${subcoImportsBaseUrl}/import-schedules/:job_id`, importPollHandler)
  server.get(`${teacherImportsBaseUrl}/import-schedules/:job_id`, importPollHandler)
}

export default async function (config) {
  const finalConfig = {
    ...config,
    interceptor: new MSWInterceptor(),
    models: mapToCamelCasedKeys(mirageModels),
    factories: mapToCamelCasedKeys(mirageFactories),
    scenarios: mapToCamelCasedKeys(mirageScenarios),
    serializers: mapToCamelCasedKeys(mirageSerializers),
    fixtures: { 'bookLibrary/readingScales': readingScalesFixture, assignments: assignmentsFixture },
    inflector: {
      pluralize,
      singularize,
    },
    routes() {
      gravity(this)
      bookLibrary(this)
      this.get(ENV.appRefresher.releaseChecker.upUrl, () => ({ sha: ENV.APP.emberGitSha }))

      setTeacherUiEndpoints(this)
      setAuth(this)
      setLessonsAPIEndpoints(this)
      setSharedReadingeggsEndpoints(this)
      setImportStudentEndpoints(this)

      setStudentEventsEndpoints(this)
      setAssignmentEndpoints(this)
      setReportingEndpoints(this)
      setLessonStandardsEndpoints(this)
      setWritingLegendsEndpoints(this)
      setRecentActivityEndpoints(this)
      setUpcomingAssignmentsEndpoints(this)
      setClassesAtAGlanceEndpoints(this)
      setNeedsAttentionStudentsEndpoints(this)

      // student-progress
      this.post(ENV.studentProgressV3Url, studentProgressRoute)
      this.post(join(gravityV3Url, 'teacher-ui/progression/api-get').href, studentProgressRoute)

      this.passthrough(contentfulEntriesProxyUrl({ preview: false }))
      this.passthrough(contentfulEntriesProxyUrl({ preview: true }))
      this.passthrough('https://app.posthog.com/*')
      this.passthrough('https://api.mathseedsprime.com/progression.json')
      this.passthrough('https://3plearning--uat.sandbox.my.salesforce-sites.com/*')
      this.passthrough('https://3plearning.my.salesforce-sites.com/*')
      // for previews
      this.passthrough('https://assets.readingeggs.com/**')
      this.passthrough('https://books.readingeggs.com/**')
      this.passthrough('https://videos.readingeggs.com/**')
      this.passthrough('https://assets.static.readingeggs.com/**')
      this.passthrough('https://books.static.readingeggs.com/**')
      this.passthrough('https://videos.static.readingeggs.com/**')
      this.passthrough('https://content-assets.static.readingeggs.com/**')
      this.passthrough('https://caper-activities.static.readingeggs.com/**')
      this.passthrough('https://jester-activities.static.readingeggs.com/**')
      // dev servers for activity previews
      if (ENV.contentLoader.caperTomfoolery.releaseSha === 'development') {
        this.passthrough(
          join(ENV.contentLoader.caperTomfoolery.assetHosts.assets, '/**').href,
          join(ENV.contentLoader.caperTomfoolery.assetHosts.books, '/**').href,
          join(ENV.contentLoader.caperTomfoolery.assetHosts.code, '/**').href,
          join(ENV.contentLoader.caperTomfoolery.assetHosts.video, '/**').href,
        )
      }
      if (ENV.contentLoader.jester.activities.releaseSha === 'development') {
        this.passthrough(
          join(ENV.contentLoader.jester.assetHosts.assets, '/**').href,
          join(ENV.contentLoader.jester.assetHosts.books, '/**').href,
          join(ENV.contentLoader.jester.assetHosts.code, '/**').href,
          join(ENV.contentLoader.jester.assetHosts.video, '/**').href,
        )
      }

      this.passthrough('https://fonts.gstatic.com/**')
      this.passthrough('http://localhost:4050/snappy-api/**')
    },
  }
  const server = createServer(finalConfig)
  await server.start()
  return server
}
